/**
 * SEO & publishing panel of the topic workspace (P1-AUSGABE): shows the persisted output
 * fields of a v2 article (subtitle, slug, meta description, primary keyword, byline, CTA,
 * number lint) and lets the editor change the four editable ones via the article PATCH.
 * The body is sent unchanged (PATCH stores it verbatim).
 */
import { useState } from "react";
import { Loader2, Pencil, Save, X } from "lucide-react";
import { api } from "../../api";
import { useT } from "../../i18n";
import { useToast } from "../../ui";

export interface SeoFields {
  id: string;
  body_md: string;
  subtitle?: string | null;
  slug?: string | null;
  meta_description?: string | null;
  primary_keyword?: string | null;
  author_name?: string | null;
  reviewed_by?: string | null;
  cta_variant?: string | null;
  lint?: { numbers?: { unsupported?: string[] }; anchors?: { unresolved?: string[]; key_mismatch?: string[] } } | null;
}

type Editable = "subtitle" | "slug" | "meta_description" | "primary_keyword";
const EDITABLE: Editable[] = ["primary_keyword", "slug", "meta_description", "subtitle"];

export default function SeoPanel<A extends SeoFields>({
  article,
  onSaved,
}: {
  article: A;
  onSaved: (a: A) => void;
}) {
  const t = useT().blog.seo;
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Record<Editable, string>>({
    subtitle: "", slug: "", meta_description: "", primary_keyword: "",
  });

  const label: Record<Editable, string> = {
    subtitle: t.subtitle, slug: t.slug, meta_description: t.meta, primary_keyword: t.primaryKeyword,
  };
  const start = () => {
    setDraft({
      subtitle: article.subtitle ?? "",
      slug: article.slug ?? "",
      meta_description: article.meta_description ?? "",
      primary_keyword: article.primary_keyword ?? "",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const a = await api.patch<A>(`/api/blog/v2/articles/${article.id}`, {
        body_md: article.body_md,
        ...draft,
      });
      onSaved(a);
      setEditing(false);
      toast.ok(t.saved);
    } catch (e) {
      toast.err(t.saveFailed, String(e));
    } finally {
      setSaving(false);
    }
  };

  const unsupported = article.lint?.numbers?.unsupported ?? [];
  // P9-UI: topic anchors the article could not use (not released / key mismatch) — a curation note, no block.
  const unusedAnchors = [...(article.lint?.anchors?.unresolved ?? []), ...(article.lint?.anchors?.key_mismatch ?? [])];
  const meta = article.meta_description ?? "";

  return (
    <div className="rounded-2xl border border-cream-300 dark:border-ink-600 bg-cream-50/60 dark:bg-ink-800/50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-display text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-text-2">
          {t.title}
        </div>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setEditing(false)}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-control border border-border px-2 py-1 text-xs text-text-2 hover:bg-surface-3 disabled:opacity-40"
            >
              <X className="h-3 w-3" /> {t.cancel}
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1 rounded-control bg-accent-500 px-2 py-1 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} {t.save}
            </button>
          </div>
        ) : (
          <button
            onClick={start}
            className="inline-flex items-center gap-1 rounded-control border border-border px-2 py-1 text-xs text-text-2 hover:bg-surface-3"
          >
            <Pencil className="h-3 w-3" /> {t.edit}
          </button>
        )}
      </div>

      <dl className="space-y-2 text-xs">
        {EDITABLE.map((k) => (
          <div key={k}>
            <dt className="text-text-3">
              {label[k]}
              {k === "meta_description" && (
                <span className="ml-1.5 font-mono">
                  {t.chars((editing ? draft.meta_description : meta).length)}
                </span>
              )}
            </dt>
            <dd className="mt-0.5 text-text-2 break-words">
              {editing ? (
                k === "meta_description" ? (
                  <textarea
                    value={draft[k]}
                    onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                    rows={3}
                    className="w-full rounded-md border border-cream-300 dark:border-ink-600 bg-white dark:bg-ink-900 p-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                  />
                ) : (
                  <input
                    value={draft[k]}
                    onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                    className="w-full rounded-md border border-cream-300 dark:border-ink-600 bg-white dark:bg-ink-900 p-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                  />
                )
              ) : (
                (article[k] as string | null | undefined) || <span className="text-text-3">{t.absent}</span>
              )}
            </dd>
          </div>
        ))}
        <div>
          <dt className="text-text-3">{t.byline}</dt>
          <dd className="mt-0.5 text-text-2">
            {article.author_name || t.absent}
            {article.reviewed_by ? ` · ${t.reviewedBy(article.reviewed_by)}` : ""}
          </dd>
        </div>
        <div>
          <dt className="text-text-3">{t.cta}</dt>
          <dd className="mt-0.5 font-mono text-text-2">{article.cta_variant || t.ctaMissing}</dd>
        </div>
        <div className={unsupported.length ? "text-amber-600" : "text-emerald-600"}>
          {unsupported.length ? `${t.lintNumbers(unsupported.length)}: ${unsupported.join(", ")}` : t.lintOk}
        </div>
        {unusedAnchors.length > 0 && (
          <div className="text-amber-600">{`${t.lintAnchors(unusedAnchors.length)}: ${unusedAnchors.join(", ")}`}</div>
        )}
      </dl>
    </div>
  );
}
